<template>
    <BaseForm @submit.prevent>
        <template #fields>
            <FieldGroup :title="$t('identifiers')">
                <TextField
                    :value="values.nameShort"
                    :label="$t('name-short')"
                    :error-messages="errors.nameShort"
                    :required="true"
                    @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameShort', true)"
                />
                <TextField
                    :value="values.nameLong"
                    :label="$t('name-long')"
                    :error-messages="errors.nameLong"
                    :required="true"
                    @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameLong', true)"
                />
                <NumberField
                    :value="values.executionYear ?? ''"
                    :label="$t('execution-year')"
                    :error-messages="errors.executionYear"
                    :required="true"
                    @input="setFieldValue('executionYear', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('executionYear', true)"
                />
                <ComboboxField
                    :value="values.status"
                    :label="$t('status')"
                    :error-messages="errors.status"
                    :items="getProjectStatusComboboxItems()"
                    :required="true"
                    @change="setFieldValue('status', $event)"
                    @blur="setFieldTouched('status', true)"
                />
            </FieldGroup>
        </template>
        <template #buttons>
            <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard') }}
            </Button>
            <Button v-else size="Base" variant="Neutral-Light" @click="$router.back">
                <template #icon>
                    <CloseIcon class="size-5" />
                </template>
                {{ $t('abort') }}
            </Button>
            <Button
                size="Base"
                variant="Info-Strong"
                :disabled-explanation="saveButtonDisabledExplanation"
                type="submit"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('create') }}
            </Button>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import Button from '@/components/Button/Button.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import ComboboxField from '@/components/Form/ComboboxField.vue';
import FieldGroup from '@/components/Form/FieldGroup.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextField from '@/components/Form/TextField.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import { useForm } from '@/forms/composables/useForm';
import { useProjectCreate } from '@/project/composables/useProjectCreate';
import {
    DEFAULT_PROJECT_STATUS,
    getProjectStatusComboboxItems,
    getProjectStatusLabel,
    goToProject,
} from '@/project/project';
import { ProjectCreateSchema } from '@/project/schemas';
import { computed } from 'vue';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);

const {
    values,
    errors,
    setFieldTouched,
    setFieldValue,
    validate,
    isTouched,
    saveButtonDisabledExplanation,
    onDiscard,
} = useForm(
    ProjectCreateSchema,
    computed(() => ({
        nameShort: '',
        nameLong: '',
        status: { id: DEFAULT_PROJECT_STATUS, name: getProjectStatusLabel(DEFAULT_PROJECT_STATUS) },
        executionYear: '',
    })),
);

const { create } = useProjectCreate(seriesId, values);

async function onSave() {
    const result = await validate();
    if (result) {
        const { id } = await create();
        if (id) {
            goToProject(id);
        }
    }
}
</script>
